import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms"; // Add import
import {
  // CloseOutlined, // Keep for potential future use if needed, but remove from current layout
  EllipsisOutlined,
  // HeartFilled, // Remove unused import
  LikeFilled,
  MessageFilled, // Use Book icon for save/bookmark
  UserOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
// import { formatDistanceToNow } from "date-fns"; // Remove unused date import
import { useMemo } from "react";
import { FaShare } from "react-icons/fa"; // Import FaShare
// import { FaRegComment } from "react-icons/fa"; // Remove unused import
import CaptionExpand from "./components/CaptionExpand"; // Import CaptionExpand
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface TikTokCardProps extends BaseSocialCardProps {
  title?: string;
  platformId: string; // Add platformId prop
  profileUrl?: string; // Re-add profileUrl prop
}

const TikTokCard = ({
  caption,
  mediaUrls = [],
  username = "User", // Default placeholder
  profileUrl, // Re-add profileUrl to destructuring
  platformId, // Destructure platformId
  isTemplateVideo,
}: TikTokCardProps) => {
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  const displayCaption = useMemo(() => {
    // Truncate logic remains the same for now
    if (captionLimit && caption.length > captionLimit) {
      return caption.substring(0, captionLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  const hasMedia = mediaUrls && mediaUrls.length > 0;

  return (
    // Adjust layout: Only flex, width, height on main container
    <div className="flex w-full max-w-[600px] h-[700px]">
      {/* Media & Text Container (Left Side) - Apply container styles here */}
      <div className="relative flex-grow h-full bg-antd-colorBgContainer text-antd-colorText rounded-xl overflow-hidden shadow-md">
        {/* Media Background */}
        {hasMedia && (
          <div className="absolute inset-0 z-0">
            <MediaSection
              mediaUrls={mediaUrls}
              isTemplateVideo={isTemplateVideo}
            />
            <div className="absolute inset-0 bg-black/10"></div>
          </div>
        )}
        {!hasMedia && (
          <div className="absolute inset-0 bg-antd-colorBgContainer flex items-center justify-center text-antd-colorTextDisabled">
            No Media
          </div>
        )}

        {/* Top Controls (Optional: Keep Ellipsis for desktop?) */}
        <div className="absolute top-0 right-0 p-3 z-10">
          {/* <SoundOutlined className="text-lg text-white" /> */}
          <EllipsisOutlined className="text-xl text-white cursor-pointer" />
        </div>

        {/* Bottom Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-30 bg-gradient-to-t from-black/60 via-black/30 to-transparent">
          {/* User Info - Just username now */}
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-sm text-white">
              @{username}
            </span>
          </div>

          {/* Caption */}
          {displayCaption && (
            <div className="font-normal">
              <CaptionExpand
                text={displayCaption}
                initialLines={1} // Limit initial lines
                showMoreText="...more"
                showLessText=" less"
                linkColor="white" // Link color for "more/less"
                className="text-sm text-white" // Base text color
                buttonBackgroundClass="bg-transparent" // Ensure button bg is transparent
              />
            </div>
          )}

          {/* Optional: Music/Sound Info */}
          {/* <div className="mt-1 text-xs flex items-center gap-1">
             <MusicNoteIcon className="h-3 w-3" />
             <span>Original Sound - {username}</span>
           </div> */}
        </div>

        {/* Right Side Actions Panel - Now inside the media container */}
        <div className="absolute bottom-4 right-4 flex flex-col items-center w-auto z-20">
          {" "}
          {/* Adjusted positioning and width */}
          {/* Profile Pic with Follow + */}
          <div className="relative mb-4">
            <Avatar
              size={48}
              src={profileUrl}
              icon={!profileUrl ? <UserOutlined /> : undefined}
              className="border-2 border-antd-colorBorder"
            />
            {/* Placeholder Follow Icon */}
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center border-2 border-antd-colorBgContainer">
              <span className="text-white text-xs font-bold">+</span>
            </div>
          </div>
          {/* Like Button */}
          <div className="flex flex-col items-center gap-1 mb-3 text-antd-colorText">
            <div className="w-10 h-10 rounded-full bg-antd-colorFillSecondary flex items-center justify-center">
              <LikeFilled className="text-xl" />
            </div>
            <span className="text-xs font-semibold">1.2M</span>
          </div>
          {/* Comment Button */}
          <div className="flex flex-col items-center gap-1 mb-3 text-antd-colorText">
            <div className="w-10 h-10 rounded-full bg-antd-colorFillSecondary flex items-center justify-center">
              <MessageFilled className="text-xl" />
            </div>
            <span className="text-xs font-semibold">10.5K</span>
          </div>
          {/* Share Button */}
          <div className="flex flex-col items-center gap-1 mb-4 text-antd-colorText">
            <div className="w-10 h-10 rounded-full bg-antd-colorFillSecondary flex items-center justify-center">
              <FaShare className="text-xl" />
            </div>
            <span className="text-xs font-semibold">8.1K</span>
          </div>
          {/* Placeholder Sound/Music Icon (Spinning) */}
          {/* <div className="mt-auto animate-spin-slow">
             <Avatar size={40} src={'/path/to/record-placeholder.png'} />
           </div> */}
        </div>
      </div>
    </div>
  );
};

export default TikTokCard;
